import { useEffect, useState } from 'react';
import { useClerk, useSignIn, useSignUp } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

const SsoCallback = () => {
    const clerk = useClerk();
    const { signIn } = useSignIn();
    const { signUp, setActive } = useSignUp();
    const navigate = useNavigate();
    const [error, setError] = useState('');

    useEffect(() => {
        const finish = (sessionId?: string | null) => {
            if (sessionId) {
                setActive?.({ session: sessionId }).catch(() => {});
            }
            navigate('/login', { replace: true });
        };

        const completeSignUp = async () => {
            if (!signUp) return null;
            let su: any = signUp;

            // If the sign-up is still missing required fields, auto-fill the common ones
            // (Microsoft/Google don't supply a username or a legal-consent flag) so the
            // flow never lands on #/continue.
            if (su.status === 'missing_requirements') {
                const missing: string[] = su.missingFields || [];
                console.log('SSO debug - missingFields:', missing);
                const updates: any = {};
                if (missing.includes('username')) {
                    updates.username = su.emailAddress || `user${Math.floor(Math.random() * 100000)}`;
                }
                if (missing.includes('legal_accepted')) {
                    updates.legalAccepted = true;
                }
                if (Object.keys(updates).length) {
                    try { su = await su.update(updates); } catch (e) { console.error('signUp.update failed:', e); }
                }
            }
            return su?.createdSessionId || null;
        };

        const handle = async () => {
            try {
                await clerk.handleRedirectCallback({
                    signInForceRedirectUrl: '/login',
                    signUpForceRedirectUrl: '/login',
                    transferable: true,
                });
            } catch (err: any) {
                if (err?.errors?.[0]?.code === 'session_exists') return finish();
                console.error('SSO callback error:', err);
            }

            if (clerk.session) return finish();

            // Existing account signed in via OAuth
            if (signIn?.createdSessionId) return finish(signIn.createdSessionId);

            // New account: transfer the OAuth credential into a sign-up
            const transferable = signIn?.firstFactorVerification?.status === 'transferable';
            if (transferable && signUp) {
                try {
                    const res: any = await signUp.create({ transfer: true });
                    if (res.status === 'complete' && res.createdSessionId) return finish(res.createdSessionId);
                    // Sign-up created but still needs fields — auto-fill and finish
                    const sessionId = await completeSignUp();
                    if (sessionId) return finish(sessionId);
                    console.error('Sign-up transfer incomplete:', res.status, res.missingFields);
                } catch (e) {
                    console.error('Sign-up transfer failed:', e);
                }
            } else if (signUp?.status === 'missing_requirements') {
                const sessionId = await completeSignUp();
                if (sessionId) return finish(sessionId);
            }

            finish();
        };
        handle();
    }, []);

    if (error) return <div className="flex items-center justify-center h-screen"><p className="text-red-500">{error}</p></div>;
    return <div className="flex items-center justify-center h-screen"><p className="text-gray-500">Processing SSO login...</p></div>;
};

export default SsoCallback;
